<template>
  <div class="club-profile">
    <div class="profile-header">
      <div class="header-content">
        <div class="club-avatar" :style="clubAvatarStyle">
          <template v-if="clubInfo.avatar">
            <img :src="clubInfo.avatar" style="width: 100%; height: 100%; border-radius: 50%; background-color: white; border: 1px solid #e0e0e0;" />
          </template>
          <template v-else>
            {{ clubInfo.name ? clubInfo.name.charAt(0).toUpperCase() : '' }}
          </template>
          
        </div>
        <div class="club-basic-info">
          <h1 class="club-name">
            {{ clubInfo.name }}
          </h1>
          <div class="club-types-container" v-if="parsedClubTypes.length > 0">
            <span v-for="(type, index) in parsedClubTypes" :key="index" class="club-type">
              {{ type }}
            </span>
          </div>
          <p class="club-account" v-if="clubInfo.account_name">@{{ clubInfo.account_name }}</p>
          <p class="club-intro">{{ clubInfo.intro }}</p>
        </div>
      </div>
    </div>

    <div class="profile-content">
      <el-row :gutter="20">
        <el-col :span="16">
          <el-card class="info-card">
            <div slot="header" class="card-header">
              <span>Club Information</span>
            </div>
            <div class="info-list">
              <div class="info-item">
                <span class="label">Advisor:</span>
                <template v-if="clubInfo.advisors && clubInfo.advisors.length">
                  <div class="value">
                    <div v-for="(advisor, index) in clubInfo.advisors" :key="index">
                      {{ advisor.contact_enName }}
                    </div>
                  </div>
                </template>
                <span class="value" v-else>Not assigned</span>
              </div>
              
            </div>
          </el-card>

          <el-card class="leadership-card" v-if="clubInfo.leadership?.length">
            <div slot="header" class="card-header">
              <span>Club Leadership</span>
            </div>
            <div class="leadership-list">
              <div v-for="leader in clubInfo.leadership" :key="leader.position" class="leadership-item">
                <div class="position">{{ leader.position }}</div>
                <div class="leader-info" v-if="leader.user">
                  {{ leader.user.student_lastName_pinyin && leader.user.student_givenName_pinyin ? 
                     leader.user.student_givenName_pinyin + ' ' + leader.user.student_lastName_pinyin :
                     leader.user.student_givenName + ' ' + leader.user.student_lastName }}
                </div>
              </div>
            </div>
          </el-card>
        </el-col>

        <el-col :span="8">
          <el-card class="stats-card">
            <div slot="header" class="card-header">
              <span>Quick Stats</span>
            </div>
            <div class="stats-list">
              <div class="stat-item">
                <div class="stat-value">{{ clubInfo.member_count || 0 }}</div>
                <div class="stat-label">Members</div>
              </div>
            </div>
          </el-card>

          <el-card class="action-card">
            <div slot="header" class="card-header">
              <span>Join Club</span>
            </div>
            <div class="action-content">
              <p v-if="!isStudent" class="hint-text">Only students can apply to join clubs</p>
              <el-button 
                v-else
                type="primary" 
                @click="applyToClub"
                :disabled="!!clubInfo.user_info"
              >
                {{ clubInfo.user_info ? 'Already Applied/Joined' : 'Apply to Join' }}
              </el-button>
            </div>
          </el-card>
        </el-col>
      </el-row>
    </div>

    <!-- Add login dialog -->
    <el-dialog
      title="Login Required"
      :visible.sync="showLoginDialog"
      width="400px"
      :close-on-click-modal="false"
      :show-close="true"
    >
      <login-simple 
        @close="showLoginDialog = false"
        @login-success="handleLoginSuccess"
      />
    </el-dialog>
  </div>
</template>

<script>
import { postRequest } from '@/api/eclub'
import { getUserId, getUserType, isLoggedIn } from '@/utils/store'
import LoginSimple from '@/components/LoginSimple'

export default {
  name: 'SchoolClubProfile',
  components: {
    LoginSimple
  },
  data() {
    return {
      clubInfo: {},
      loading: true,
      showLoginDialog: false
    }
  },
  computed: {
    clubAvatarStyle() {
      return {
        backgroundColor: '#e6e6e6',
        width: '80px',
        height: '80px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '32px',
        fontWeight: 'bold',
        color: '#666'
      }
    },
    isStudent() {
      return !isLoggedIn() || getUserType() === 'student'
    },
    parsedClubTypes() {
      if (!this.clubInfo.type) return []

      // If it's already an array, return it
      if (Array.isArray(this.clubInfo.type)) {
        return this.clubInfo.type
      }

      // If it's a string that looks like JSON array, parse it
      if (typeof this.clubInfo.type === 'string') {
        const trimmed = this.clubInfo.type.trim()
        if (trimmed.startsWith('[')) {
          try {
            const parsed = JSON.parse(trimmed)
            return Array.isArray(parsed) ? parsed : [this.clubInfo.type]
          } catch (e) {
            return [this.clubInfo.type]
          }
        }
        // Return as single item array
        return [this.clubInfo.type]
      }

      return []
    }
  },
  created() {
    this.fetchClubInfo()
  },
  methods: {
    async fetchClubInfo() {
      const club_id = this.$route.query.club_id
      if (!club_id) {
        this.$message.error('Club ID is missing')
        return
      }

      this.loading = true
      try {
        const response = await postRequest('getClubInfo', {
          club_id,
          user_id: isLoggedIn() ? (getUserId() || '') : ''
        })
        if (response.data.code === 0) {
          this.clubInfo = response.data.data
        }
      } catch (error) {
        console.error('Error loading club info:', error)
        this.$message.error('Failed to load club information')
      } finally {
        this.loading = false
      }
    },
    applyToClub() {
      if (!isLoggedIn()) {
        this.showLoginDialog = true
        return
      }
      
      this.$router.push({
        name: 'Club Application',
        query: { 
          club_id: this.$route.query.club_id,
          club_name: this.clubInfo.name,
          club_type: this.clubInfo.type
        }
      })
    },
    handleLoginSuccess() {
      this.showLoginDialog = false
      this.fetchClubInfo()

    }
  }
}
</script>

<style lang="scss" scoped>
.club-profile {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;

  .profile-header {
    margin-bottom: 24px;

    .header-content {
      display: flex;
      align-items: flex-start;
      gap: 20px;

      .club-basic-info {
        flex: 1;

        .club-name {
          margin: 0 0 8px;
        }

        .club-types-container {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-bottom: 8px;

          .club-type {
            font-size: 13px;
            color: #666;
            background: #f5f7fa;
            padding: 6px 14px;
            border-radius: 14px;
            border: 1px solid #e4e7ed;
            white-space: nowrap;
          }
        }

        .club-account {
          color: #909399;
          margin: 4px 0;
          font-size: 18px;
        }

        .club-intro {
          color: #666;
          line-height: 1.5;
          margin: 0;
        }
      }
    }
  }

  .profile-content {
    .el-card {
      margin-bottom: 20px;
    }

    .card-header {
      font-weight: bold;
      color: #303133;
    }

    .info-list {
      .info-item {
        display: flex;
        margin-bottom: 16px;
        
        .label {
          color: #909399;
          width: 140px;
        }

        .value {
          color: #303133;
          flex: 1;
        }
      }
    }

    .leadership-list {
      .leadership-item {
        display: flex;
        margin-bottom: 12px;
        
        .position {
          width: 140px;
          color: #909399;
        }

        .leader-info {
          flex: 1;
          color: #303133;
        }
      }
    }

    .stats-list {
      text-align: center;

      .stat-item {
        .stat-value {
          font-size: 24px;
          font-weight: bold;
          color: #409EFF;
        }

        .stat-label {
          color: #909399;
          margin-top: 8px;
        }
      }
    }

    .action-card {
      .action-content {
        text-align: center;

        .hint-text {
          color: #909399;
          margin-bottom: 12px;
        }
      }
    }
  }
}
</style> 